import { fetch, useApi } from '@omnipresentgroup/api';
import type {
  DocumentPresignedDetails,
  DocumentUploadRequest,
  DocumentUploadResponse,
} from '@omnipresentgroup/types';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { API_BASE, API_DOCUMENT_STORAGE_PATH } from '../constants';

export const useDocumentUpload = () => {
  const { GET } = useApi();

  const uploadDocument = useCallback(
    async ({
      fileToUpload,
      directory = 'document',
      accept = '*/*',
    }: DocumentUploadRequest): Promise<DocumentUploadResponse> => {
      // Generate new file name using uuid
      const fileExtension = fileToUpload.name.split('.').pop();
      const fileName = `${directory}/${uuidv4()}.${fileExtension}`;
      const fileWithNewName = new File([fileToUpload], fileName, {
        type: fileToUpload.type,
      });

      // Get the presigned S3 URL and details
      const { url, fields } = await GET<DocumentPresignedDetails>(
        `${API_BASE}/${API_DOCUMENT_STORAGE_PATH}/presignedpost`,
        {},
        { filename: fileWithNewName.name },
      );

      // Upload the file to S3
      const headers = {
        Accept: accept,
      };
      const formData = {
        ...fields,
        file: fileWithNewName,
      };
      return fetch(url, {}, headers)
        .formData(formData)
        .post()
        .res()
        .then(() => {
          return {
            documentUrl: `${url}/${fields.key}`,
            originalFileName: fileToUpload.name,
            uniqueIdFileName: fileWithNewName.name,
          };
        });
    },
    [GET],
  );

  return {
    uploadDocument,
  };
};
